import React from 'react';

import { DateTime } from 'luxon';

import {
  NumberInput,
  ReferenceArrayInput,
  SelectArrayInput,
} from 'react-admin';

import { Box } from '@mui/material';

import requestTypesChoices from '../../../../constants/requestTypes';
import ReferenceEntityInputV2 from '../../../../customFields/ReferenceEntityInputV2';
import currencies from '../../../../constants/currencies';
import SmartDateTimeInput from '../../../../customFields/SmartDateTimeInput';
import dateRangeValidation from '../../../utils/reports/dateRangeValidation';
import dateParse, { dateFormat } from '../../../utils/dateParser';
import FieldBox from '../FieldBox';
import RangeFields from '../RangeFields';

const isPastDate = (value: string) => {
  if (DateTime.fromISO(value) > DateTime.now()) return 'Invalid date';
  return undefined;
};

const numberRangeValidation = (_value: Number, allValues: { report: {
  fromAmount: Number;
  toAmount: Number;
};
}) => {
  const { fromAmount, toAmount } = allValues.report;
  if (fromAmount && toAmount) {
    if (fromAmount > toAmount) {
      return 'from amount should be less than to amount';
    }
  }
  return undefined;
};

const isSafeNumber = (value: number) => {
  if (!(value >= Number.MIN_SAFE_INTEGER && value <= Number.MAX_SAFE_INTEGER)) return `Number should be between ${Number.MIN_SAFE_INTEGER} and ${Number.MAX_SAFE_INTEGER}`;
  return undefined;
};

const RequestOverview = () => (
  <Box display="flex" flexDirection="column" gap={4}>
    <FieldBox title="Date range" description="Only requests created within this date/time range are included (CET timezone). 'Date from' defaults to 2000-01-01 00:00; 'date to' defaults to generation time.">
      <RangeFields
        from={(
          <SmartDateTimeInput
            fullWidth
            source="report.fromDate"
            label="Date from"
            validate={[isPastDate, dateRangeValidation]}
            parse={dateParse}
            format={dateFormat}
            helperText={false}
          />
        )}
        to={(
          <SmartDateTimeInput
            fullWidth
            source="report.toDate"
            label="Date to"
            validate={[isPastDate, dateRangeValidation]}
            parse={dateParse}
            format={dateFormat}
            helperText={false}
          />
        )}
      />
    </FieldBox>
    <FieldBox title="Amount range" description="Only requests with an amount within this range are included. Leave either end empty for no lower/upper limit.">
      <RangeFields
        from={(
          <NumberInput fullWidth source="report.fromAmount" label="Amount from" validate={[isSafeNumber, numberRangeValidation]} helperText={false} />
        )}
        to={(
          <NumberInput fullWidth source="report.toAmount" label="Amount to" validate={[isSafeNumber, numberRangeValidation]} helperText={false} />
        )}
      />
    </FieldBox>
    <FieldBox title="Request types" description="Filter to only include requests of these types (Transaction, Individual KYC, Business KYC). Leave empty for all types.">
      <SelectArrayInput
        choices={[
          requestTypesChoices.TRANSACTION,
          requestTypesChoices.INDIVIDUAL_KYC,
          requestTypesChoices.BUSINESS_KYC,
        ]}
        fullWidth
        source="report.requestTypes"
        label=""
        helperText={false}
      />
    </FieldBox>
    <FieldBox title="Transaction types" description="Filter to only include requests with these transaction types. Leave empty for all transaction types.">
      <ReferenceArrayInput perPage={100} reference="transaction-types" source="report.transactionTypes" filter={{ active: true }}>
        <SelectArrayInput fullWidth source="report.transactionTypes" label="" helperText={false} />
      </ReferenceArrayInput>
    </FieldBox>
    <FieldBox title="Currencies" description="Filter to only include transaction requests in these currencies. Leave empty for all currencies.">
      <SelectArrayInput
        choices={currencies.map((currency) => ({
          name: currency,
          id: currency,
        }))}
        fullWidth
        source="report.currencies"
        label=""
        helperText={false}
      />
    </FieldBox>
    <FieldBox title="Signals" description="Filter to only include requests that have these signal IDs. Leave empty for all signals.">
      <ReferenceArrayInput perPage={100} reference="signals" source="report.signals" filter={{ active: true }}>
        <SelectArrayInput fullWidth source="report.signals" label="" helperText={false} />
      </ReferenceArrayInput>
    </FieldBox>
    <FieldBox title="Entity" description="Filter to only include requests related to this specific entity. Leave empty to include all entities.">
      <ReferenceEntityInputV2 label="" source="report.entity" helperText={false} />
    </FieldBox>
  </Box>
);

export default RequestOverview;
